'use client';

import { AggregateLiqState } from 'src/hooks/Liquidations/useAggregateLiquidationDataForUser';
import { Fragment, useCallback, useState } from 'react';
import TableV2 from 'src/components/Tables/TableV2';
import {
	COMMON_UI_UTILS,
	UISerializableLiquidationRecord,
} from '@drift/common';
import { getUrlForTx } from 'src/utils/explorer';
import { cj, cm } from 'src/utils/classNames';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import Collapsible from 'src/components/Collapsible/Collapsible';
import { ChevronDown, Open, Swap } from '@drift-labs/icons';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import Link from 'src/components/primitives/Link/Link';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketIcon from 'src/components/Utils/MarketIcon';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { WithChildren } from 'src/utils/UtilityTypes';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Divider from 'src/components/primitives/Divider/Divider';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';

interface LiquidationDetailsDrawerPerpsCardsProps {
	data: AggregateLiqState;
}

export const LiquidationDetailsDrawerPerpsCards = ({
	data,
}: LiquidationDetailsDrawerPerpsCardsProps) => {
	const [expandedRowIndices, setExpandedRowIndices] = useState<number[]>([]);

	const toggleExpanded = useCallback(
		(index: number) => {
			if (expandedRowIndices.includes(index)) {
				setExpandedRowIndices(
					expandedRowIndices.filter((item) => item !== index)
				);
			} else {
				setExpandedRowIndices([...expandedRowIndices, index]);
			}
		},
		[expandedRowIndices]
	);

	return (
		<TableCardsContainer>
			{data.liqdPerps.map((row, index) => {
				const expanded = expandedRowIndices.includes(index);

				const liquidationRecords = data.liquidations.filter(
					(liq) => liq.liquidatePerp?.marketIndex === row.market.marketIndex
				);

				return (
					<TableCard
						key={`${index}_${row.market.marketIndex}`}
						symbol={row.market.symbol}
					>
						<div className="flex justify-between">
							<MobileTableCard.Label>Size</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay
									value={row.baseSize}
									type="number_signed"
									suffix={row.market.baseAssetSymbol}
									trimZeroes
								/>
							</MobileTableCard.Value>
						</div>

						<div className="flex justify-between">
							<MobileTableCard.Label>Notional</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay value={row.quoteSize.abs()} type="currency" />
							</MobileTableCard.Value>
						</div>

						<div className="flex justify-between">
							<MobileTableCard.Label>Price</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay
									value={row.avgPrice}
									type="currency"
									decimals={getMarketPriceDecimals(row.market.symbol)}
								/>
							</MobileTableCard.Value>
						</div>

						<div className="flex justify-between">
							<MobileTableCard.Label>Fees</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay value={row.fee} type="currency" />
							</MobileTableCard.Value>
						</div>

						{liquidationRecords.length > 0 && (
							<Collapsible.Root
								className="w-full"
								open={expanded}
								onOpenChange={() => toggleExpanded(index)}
							>
								<Collapsible.Trigger asChild>
									<TextButton
										size="sm"
										variant="secondary"
										className={cj(
											'w-full justify-between [&_svg]:transition-transform',
											expanded && '[&_svg]:rotate-180'
										)}
										rightIcon={ChevronDown}
									>
										Trade History
									</TextButton>
								</Collapsible.Trigger>

								<Collapsible.Content className="mt-xs">
									<LiquidationDetailsDrawerPerpsSubCards
										liquidationRecords={liquidationRecords}
									/>
								</Collapsible.Content>
							</Collapsible.Root>
						)}
					</TableCard>
				);
			})}
		</TableCardsContainer>
	);
};

interface LiquidationDetailsDrawerPerpsSubCardsProps {
	liquidationRecords: UISerializableLiquidationRecord[];
}

const LiquidationDetailsDrawerPerpsSubCards = ({
	liquidationRecords,
}: LiquidationDetailsDrawerPerpsSubCardsProps) => {
	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();
	const isDevMode = useDevSwitchIsOn();

	return (
		<div className="flex flex-col gap-y-md rounded-sm bg-container-bg p-md">
			{liquidationRecords.map((record, index) => {
				return (
					<Fragment key={record.txSig}>
						{index > 0 && <Divider />}

						<div className="flex flex-col gap-y-md">
							<div className="grid w-full grid-cols-3">
								<MobileTableCard.LabelWithValue
									label="Direction"
									value={
										record.liquidatePerp.baseAssetAmount.isPos()
											? 'Long'
											: 'Short'
									}
									valueClassName={
										record.liquidatePerp.baseAssetAmount.isPos()
											? 'text-text-positive-green-button'
											: 'text-text-negative-red-button'
									}
								/>

								<MobileTableCard.LabelWithValue
									className="items-center"
									label="Filled"
									value={record.liquidatePerp.baseAssetAmount
										.abs()
										.toTradePrecision()}
								/>

								<MobileTableCard.LabelWithValue
									className="items-end"
									label="Fill Price"
									value={`$${COMMON_UI_UTILS.calculateAverageEntryPrice(
										record.liquidatePerp.quoteAssetAmount,
										record.liquidatePerp.baseAssetAmount
									).toTradePrecision(true)}`}
								/>
							</div>

							<div className="grid w-full grid-cols-2">
								<MobileTableCard.LabelWithValue
									label={
										<>
											Time
											<IconButton
												size="sm"
												variant="tertiary"
												className="-m-sm p-sm"
												onClick={toggleDateDisplayType}
												icon={Swap}
											/>
										</>
									}
									value={
										<TableV2.DateCell
											className="w-fit py-0 whitespace-nowrap"
											displayAsTimestamp={displayDatesAsTimestamp}
											date={new Date(record.ts.toNumber() * 1000)}
										/>
									}
								/>

								<MobileTableCard.LabelWithValue
									label="Tx"
									className="items-end"
									value={
										<Link
											variant="secondary"
											className="-m-sm p-sm"
											href={getUrlForTx(record.txSig)}
											openInNewTab
											rightIcon="none"
										>
											<Open size={16} />
										</Link>
									}
								/>
							</div>
							{isDevMode && (
								<MobileTableCard.LabelWithValue
									label="IF Fee / Liq Fee"
									value={`$${
										record.liquidatePerp?.ifFee?.toTradePrecision() ?? '0'
									} / $${
										record.liquidatePerp?.liquidatorFee?.toTradePrecision() ??
										'0'
									}`}
								/>
							)}
						</div>
					</Fragment>
				);
			})}
		</div>
	);
};

interface LiquidationDetailsDrawerDepositCardsProps {
	data: AggregateLiqState['liqdDeposits'];
}

export const LiquidationDetailsDrawerDepositCards = ({
	data,
}: LiquidationDetailsDrawerDepositCardsProps) => {
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();

	return (
		<TableCardsContainer>
			{data.map((row) => {
				const bankSymbol = spotMarketsSortedByMarketIndex[row.bankIndex].symbol;

				return (
					<TableCard key={row.bankIndex} symbol={bankSymbol}>
						<div className="flex justify-between">
							<MobileTableCard.Label>Size</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay
									value={row.baseSize}
									type="number_signed"
									suffix={bankSymbol}
									trimZeroes
								/>
							</MobileTableCard.Value>
						</div>
						<div className="flex justify-between">
							<MobileTableCard.Label>Notional</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay value={row.quoteSize} type="currency" />
							</MobileTableCard.Value>
						</div>
						<div className="flex justify-between">
							<MobileTableCard.Label>Price</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay
									value={row.avgPrice}
									type="currency"
									decimals={getMarketPriceDecimals(bankSymbol)}
								/>
							</MobileTableCard.Value>
						</div>
					</TableCard>
				);
			})}
		</TableCardsContainer>
	);
};

interface LiquidationDetailsDrawerPnlCardsProps {
	data: AggregateLiqState['liqdPerpPnls'];
}

export const LiquidationDetailsDrawerPnlCards = ({
	data,
}: LiquidationDetailsDrawerPnlCardsProps) => {
	return (
		<TableCardsContainer>
			{data.map((row) => {
				return (
					<TableCard key={row.market.symbol} symbol={row.market.symbol}>
						<div className="flex justify-between">
							<MobileTableCard.Label>Unrealized P&amp;L</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay value={row.pnlSize} type="currency" />
							</MobileTableCard.Value>
						</div>
						<div className="flex justify-between">
							<MobileTableCard.Label>Transfer Value</MobileTableCard.Label>
							<MobileTableCard.Value>
								<NumberDisplay value={row.transferQuoteSize} type="currency" />
							</MobileTableCard.Value>
						</div>
					</TableCard>
				);
			})}
		</TableCardsContainer>
	);
};

const TableCardsContainer = ({ children }: WithChildren) => {
	return (
		<div
			className={cm(MOBILE_TABLE_CARD_LIST_CLASSNAME, 'size-full px-0 sm:flex')}
		>
			{children}
		</div>
	);
};

interface TableCardProps extends WithChildren {
	symbol: string;
	className?: string;
}

function TableCard({ symbol, children }: TableCardProps) {
	return (
		<MobileTableCard.Root className="gap-y-md bg-main-bg">
			<div className="flex items-center gap-x-sm">
				<MarketIcon marketSymbol={symbol} sizeClass="size-4" />
				<span className="typo-t3 text-text-default">{symbol}</span>
			</div>
			<div className="flex flex-col gap-y-sm">{children}</div>
		</MobileTableCard.Root>
	);
}
